import React, { useRef, useEffect } from "react";

// Plexus animation inspired by gaming overlays, with breathing blue/purple hues
export default function PlexusBackground({ className = "", style = {} }) {
  const canvasRef = useRef(null);
  const animationRef = useRef();
  const nodes = useRef([]);
  const links = useRef([]);
  const NODE_COUNT = 36;
  const LINK_DIST = 150;
  const COLORS = [
    "#00eaff", // light blue
    "#0070f3", // blue
    "#a259f7"  // purple
  ];

  // Utility: random float in range
  const rand = (min, max) => Math.random() * (max - min) + min;

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    let width = canvas.width = canvas.offsetWidth;
    let height = canvas.height = canvas.offsetHeight;

    // Responsive resize
    const handleResize = () => {
      width = canvas.width = canvas.offsetWidth;
      height = canvas.height = canvas.offsetHeight;
    };
    window.addEventListener("resize", handleResize);

    // Initialize nodes
    nodes.current = Array.from({ length: NODE_COUNT }, () => ({
      x: rand(0, width),
      y: rand(0, height),
      vx: rand(-0.3, 0.3),
      vy: rand(-0.3, 0.3),
      radius: rand(2.8, 4.2),
      color: COLORS[Math.floor(rand(0, COLORS.length))]
    }));

    // Animation loop
    let t = 0;
    function animate() {
      ctx.clearRect(0, 0, width, height);
      t += 0.008;
      // Breathing effect for hue cycling
      const breath = 0.7 + 0.3 * Math.sin(t * 1.1);

      // Move nodes
      for (const node of nodes.current) {
        node.x += node.vx;
        node.y += node.vy;
        if (node.x < 0 || node.x > width) node.vx *= -1;
        if (node.y < 0 || node.y > height) node.vy *= -1;
      }

      // Draw links
      for (let i = 0; i < nodes.current.length; i++) {
        for (let j = i + 1; j < nodes.current.length; j++) {
          const n1 = nodes.current[i];
          const n2 = nodes.current[j];
          const dx = n1.x - n2.x;
          const dy = n1.y - n2.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < LINK_DIST) {
            // Gradient for line
            const grad = ctx.createLinearGradient(n1.x, n1.y, n2.x, n2.y);
            grad.addColorStop(0, n1.color);
            grad.addColorStop(1, n2.color);
            ctx.globalAlpha = breath * (1 - dist / LINK_DIST) * 0.7;
            ctx.strokeStyle = grad;
            ctx.lineWidth = 1.5;
            ctx.beginPath();
            ctx.moveTo(n1.x, n1.y);
            ctx.lineTo(n2.x, n2.y);
            ctx.stroke();
            ctx.globalAlpha = 1;
          }
        }
      }

      // Draw nodes
      for (const node of nodes.current) {
        ctx.save();
        ctx.beginPath();
        ctx.arc(node.x, node.y, node.radius * breath, 0, 2 * Math.PI);
        ctx.shadowColor = node.color;
        ctx.shadowBlur = 18 * breath;
        ctx.fillStyle = node.color;
        ctx.globalAlpha = 0.82 * breath;
        ctx.fill();
        ctx.restore();
      }

      animationRef.current = requestAnimationFrame(animate);
    }
    animate();
    return () => {
      cancelAnimationFrame(animationRef.current);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className={"plexus-bg-canvas " + className}
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        zIndex: 0,
        pointerEvents: "none",
        ...style
      }}
      aria-hidden="true"
    />
  );
}
